// RigSentinel Context Engine — Phase RCE-1
//
// EVENT BUS
// ---------
// Lightweight pub/sub bus designed into the architecture from Day 1 so future
// modules (AI Report Professionalizer, Operational Timeline, Dashboards,
// Notifications) can subscribe to context changes without architectural
// refactors. Phase RCE-1 only defines the event names + emit/subscribe API —
// existing modules continue to write to localStorage exactly as today.
//
// Pure in-memory bus. No persistence. Listeners are local to the page so
// reloads start with an empty subscription list (intentional — the engine
// itself is stateless).

/** Canonical event names. NEVER reorder or rename — future modules subscribe
 *  by string. Add new events at the end. */
export const RCE_EVENTS = Object.freeze({
  ENGINEERING_SESSION_CHANGED: "rce/engineeringSessionChanged",
  PROJECT_CHANGED:             "rce/projectChanged",
  WELL_CHANGED:                "rce/wellChanged",
  RIG_CHANGED:                 "rce/rigChanged",
  BHA_CHANGED:                 "rce/bhaChanged",
  DRILL_STRING_CHANGED:        "rce/drillStringChanged",
  MUD_PROGRAM_CHANGED:         "rce/mudProgramChanged",
  CASING_PROGRAM_CHANGED:      "rce/casingProgramChanged",
  CURRENT_OPERATION_CHANGED:   "rce/currentOperationChanged",
  CURRENT_HOLE_SECTION_CHANGED:"rce/currentHoleSectionChanged",
  CURRENT_DEPTH_CHANGED:       "rce/currentDepthChanged",
  OVERRIDE_SET:                "rce/overrideSet",
  OVERRIDE_CLEARED:            "rce/overrideCleared",
  MIGRATION_COMPLETED:         "rce/migrationCompleted",
});

const _listeners = new Map();  // event → Set<fn>

/** Subscribe to an RCE event. Returns an unsubscribe function. */
export function on(event, fn) {
  if (typeof fn !== "function") return () => {};
  if (!_listeners.has(event)) _listeners.set(event, new Set());
  _listeners.get(event).add(fn);
  return () => off(event, fn);
}

/** Remove a subscriber. */
export function off(event, fn) {
  const set = _listeners.get(event);
  if (set) set.delete(fn);
}

/**
 * Emit an event. Listener errors are isolated — one bad listener does not
 * stop the rest from running. RCE-1 publishes events from session.js and
 * overrides.js only; existing modules are NOT wired yet (will land in
 * RCE-2 .. RCE-6).
 */
export function emit(event, payload) {
  const set = _listeners.get(event);
  if (!set || set.size === 0) return;
  for (const fn of [...set]) {
    try { fn(payload, event); }
    catch (e) { try { console.warn("[RCE event listener threw]", event, e); } catch { /* noop */ } }
  }
}

/** Test/diagnostic — clear every subscriber. Never called from app code. */
export function _clearAllListeners() {
  _listeners.clear();
}

/** Test/diagnostic — return the current listener count for an event. */
export function _listenerCount(event) {
  return _listeners.get(event)?.size || 0;
}
